import { FC } from 'react';
import { ICaremapNoteResponse } from '../model/NotesClient';
import { IRecording } from '../../recording/RecordingState';
import { IonItem, IonLabel } from '@ionic/react';
import { CareMapNotesListTitle } from './CareMapNotesListTitle';
import { CareMapNotesListContent } from './CareMapNotesListContent';
import { AttachmentList } from '../generic-notes/attachments/AttachmentList';
import { RecordingInfo } from '../../recording/RecordingInfo';
import { NotesListActionItemCount } from './NotesListActionItemCount';
import { NotesListTaskCount } from './NotesListTaskCount';
import { NotesListSummarizedByAI } from './NotesListSummarizedByAI';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useNoteTaskCount } from '../../tasks/useNoteTaskCount';
import { ReactionsRow } from '../reactions/ReactionsRow';
import { HistoryViewModel } from '../../support/HistoryViewModel';
import { OneLineTagChips } from '../../tags/OneLineTagChips';

const useStyles = createUseStyles({
    recordingInfoWrapper: {
        display: 'flex',
        alignItems: 'center',
        gap: 14,

        // ensure no wrapping on smaller phones
        // TODO: avoid "items" getting clipped
        marginRight: -35,

        // NOTE: don't make this a common practice, of media queries in between breakpoints. very specific case (School Team Member Detail)
        '@media (max-width: 389px)': {
            gap: 7
        }
    },

    interactions: {
        marginTop: 8
    }
});

export const CareMapNotesListItemRecording: FC<{ recording: IRecording; taskCount?: number }> = ({ recording, taskCount }) => {
    const classes = useStyles();
    return (
        <div className={classes.recordingInfoWrapper}>
            <RecordingInfo recording={recording} />
            <NotesListSummarizedByAI recording={recording} />
            {taskCount && taskCount > 0 ? (
                <NotesListTaskCount taskCount={taskCount} />
            ) : (
                <NotesListActionItemCount recording={recording} />
            )}
        </div>
    );
};

export type ICareMapNotesListItemProps = {
    note: ICaremapNoteResponse;
    onClick?: () => void;
    button?: boolean;
    detail?: boolean;
    showPrivacy?: boolean;
    showNoteTargetHeader?: boolean;
    lines?: 'full' | 'inset' | 'none';
    sortBy?: 'updatedAt' | 'createdAt';
    searchText?: string;
    isPinned?: boolean;
    // Trimmed density (used on the home Recent Summaries): hide tags and clamp the snippet tighter.
    showTags?: boolean;
    snippetLines?: number;
    // Shows the reactions row, whose comment button calls this; the trimmed density leaves it out.
    onAddComment?: () => void;
};

type ICareMapNotesListItemSummaryProps = Required<Pick<ICareMapNotesListItemProps,
    'note' | 'showPrivacy' | 'sortBy' | 'searchText' | 'isPinned' | 'showTags' | 'snippetLines'
>> & Pick<ICareMapNotesListItemProps, 'showNoteTargetHeader'>;

// The note's title, snippet and tags.
const CareMapNotesListItemSummary: FC<ICareMapNotesListItemSummaryProps> = ({
    note,
    showPrivacy,
    showNoteTargetHeader,
    sortBy,
    searchText,
    isPinned,
    showTags,
    snippetLines
}) => (
    <>
        <CareMapNotesListTitle
            note={note}
            showPrivacy={showPrivacy}
            showNoteTargetHeader={showNoteTargetHeader}
            sortBy={sortBy}
            searchText={searchText}
            isPinned={isPinned}
        />

        <CareMapNotesListContent note={note} searchText={searchText} snippetLines={snippetLines} />

        {showTags && <OneLineTagChips tags={note.tags} />}
    </>
);

// The reactions, comment button and counts under a note in the list. Its controls are its own, so a
// tap anywhere in it must not fall through to the item and open the note.
const CareMapNotesListItemInteractions: FC<{
    note: ICaremapNoteResponse;
    onAddComment: () => void;
    onOpenNote?: () => void;
}> = ({ note, onAddComment, onOpenNote }) => {
    const classes = useStyles();
    const history = useInstance(HistoryViewModel);

    // a reader asked to sign in comes back to this list
    const { pathname, search } = history.getCurrentLocation();
    const authenticatedRedirect = `${pathname}${search}`;

    return (
        <div className={classes.interactions} onClick={e => e.stopPropagation()}>
            <ReactionsRow
                note={note}
                surface="notes_list"
                authenticatedRedirect={authenticatedRedirect}
                addComment={onAddComment}
                commentCntClickHandler={onOpenNote}
                commentCount={note.commentCount}
            />
        </div>
    );
};

export const CareMapNotesListItem: FC<ICareMapNotesListItemProps> = ({
    note,
    onClick,
    button = true,
    detail = true,
    showPrivacy = true,
    showNoteTargetHeader,
    lines,
    sortBy = 'updatedAt',
    searchText = '',
    isPinned = false,
    showTags = true,
    snippetLines = 2,
    onAddComment
}) => {
    const recording = note.otherAttributes.recording as IRecording;
    const taskCount = useNoteTaskCount(note.noteId);

    return (
        <IonItem
            button={button}
            detail={detail}
            lines={lines}
            onClick={onClick}
            data-type="note-item"
            data-node-id={note.noteId}
            style={{'--border-radius': '8px'}}
        >
            <IonLabel class="ion-text-wrap">
                <CareMapNotesListItemSummary
                    note={note}
                    showPrivacy={showPrivacy}
                    showNoteTargetHeader={showNoteTargetHeader}
                    sortBy={sortBy}
                    searchText={searchText}
                    isPinned={isPinned}
                    showTags={showTags}
                    snippetLines={snippetLines}
                />

                {!recording && (
                    <AttachmentList
                        note={note}
                        attachments={note.attachments}
                    />
                )}

                <CareMapNotesListItemRecording recording={recording} taskCount={taskCount} />

                {onAddComment && (
                    <CareMapNotesListItemInteractions note={note} onAddComment={onAddComment} onOpenNote={onClick} />
                )}
            </IonLabel>
        </IonItem>
    );
};
